// src/hooks/useCalendarQueries.js
// React Query hooks for the Calendar (read model of Work)
// -----------------------------------------------------------------------------
// Depends on:
//   - @tanstack/react-query (v5)
//   - src/services/calendarService.js
//
// Exposes:
//   - CAL_KEYS
//   - useCalendarEvents(params, options)
//   - useWorkByRange({ start, end, ...filters }, options)
//   - useAssigneeCalendar(assigneeId, params, options)
//   - invalidateCalendarRange(queryClient, params)
//
// Notes:
//   - URL/FilterBar can provide: from, to, q, status, priority, type,
//       assigneeId|assignee, clientId|client, assigneeName, clientName, includeCancelled.
//   - We normalize aliases for the **query key** so caching/invalidation stay
//     consistent regardless of which alias the caller used.
//   - Actual network param normalization happens in calendarService.listEvents.
//   - Only include includeCancelled in the key when it is explicitly true.
// -----------------------------------------------------------------------------

import { useMemo, useEffect, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  listEvents,
  defaultRange,
  groupEventsByDay,
  computeDurationMinutes,
} from '../services/calendarService';

/* ------------------------------ debug toggles ------------------------------ */

const DEBUG = String(process.env.REACT_APP_CALENDAR_DEBUG || '').toLowerCase() === 'true';
const FALLBACK_FILTERS =
  String(process.env.REACT_APP_CALENDAR_FALLBACK_FILTERS || '').toLowerCase() === 'true';

function dlog(label, obj) {
  if (!DEBUG) return;
  try {
    // small safeguard to avoid noisy circular logs
    console.log(`[calendar:${label}]`, obj ?? '');
  } catch {
    console.log(`[calendar:${label}]`, String(obj));
  }
}

/* ------------------------------ small helpers ------------------------------ */

/** Debounce any primitive value (e.g., ISO string) by N ms. */
function useDebouncedValue(value, delay = 200) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/** Normalize filter aliases & shapes for stable cache keys (no network impact). */
function normalizeForKey(raw = {}) {
  const toArray = (v) => {
    if (v == null) return undefined;
    if (Array.isArray(v)) return v.filter(Boolean);
    if (typeof v === 'string') {
      const arr = v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      return arr.length ? arr : undefined;
    }
    return [v].filter(Boolean);
  };

  const includeCancelled =
    typeof raw.includeCancelled === 'string'
      ? raw.includeCancelled.toLowerCase() === 'true'
      : raw.includeCancelled === true;

  // ---- alias mapping (IDs & names) ----
  const assignee = raw.assignee ?? raw.assigneeId ?? undefined;    // id (legacy)
  const client = raw.client ?? raw.clientId ?? undefined;          // id (legacy)
  const assigneeName = raw.assigneeName ?? undefined;              // name filter
  const clientName = raw.clientName ?? undefined;                  // name filter

  // ---- alias mapping for search term ----
  // Accept q | search | title | query | keyword | term
  const firstNonEmpty = (vals) =>
    vals.find((v) => typeof v === 'string' && v.trim() !== '');
  const qAlias = firstNonEmpty([
    raw.q,
    raw.search,
    raw.title,
    raw.query,
    raw.keyword,
    raw.term,
  ]);
  const q = qAlias ? qAlias.trim() : undefined;

  const normalized = {
    // time window (strings)
    from: raw.from,
    to: raw.to,

    // unified search term
    q,

    // entity filters for key stability
    assignee,
    client,
    assigneeName,
    clientName,

    // type (task|job|appointment)
    type: raw.type,

    // arrays
    status: toArray(raw.status),
    priority: toArray(raw.priority),

    // only include when true to avoid key churn
    ...(includeCancelled ? { includeCancelled: true } : {}),
  };

  // shallow clean for null/empty
  const out = {};
  Object.entries(normalized).forEach(([k, v]) => {
    if (v == null) return;
    if (typeof v === 'string' && v.trim() === '') return;
    if (Array.isArray(v) && v.length === 0) return;
    out[k] = v;
  });
  return out;
}

/** Create a stable, shallow-sorted key string from params. */
function stableKey(obj = {}) {
  const entries = Object.entries(obj).filter(
    ([, v]) => v !== undefined && v !== null
  );
  entries.sort(([a], [b]) => (a > b ? 1 : a < b ? -1 : 0));
  const normalized = entries.map(([k, v]) => [k, Array.isArray(v) ? v.join('|') : v]);
  return JSON.stringify(normalized);
}

export const CAL_KEYS = {
  root: ['calendar'],
  list: (params = {}) => ['calendar', 'events', stableKey(params)],
};

/* ----------------------- fallback filter (debug aid) ----------------------- */
/** These matchers let us verify whether the server honored filters.
 *  If the server ignores them, we can (optionally) enforce them locally
 *  so the UI still looks correct while we fix the backend/encoding.
 */

function matchesAssignee(e, ids) {
  if (!ids || ids.length === 0) return true;
  // normalize event assignee ids from various shapes
  const raw =
    e.assigneeIds ||
    e.assignees ||
    e.assignee ||
    e.work?.assignees ||
    [];
  const eventIds = new Set(
    (Array.isArray(raw) ? raw : [raw])
      .map((a) => (a?.id || a?._id || a))
      .filter(Boolean)
      .map(String)
  );
  return ids.some((x) => eventIds.has(String(x)));
}

function matchesClient(e, ids) {
  if (!ids || ids.length === 0) return true;
  const eid = String(e.clientId || e.client?.id || e.client?._id || '');
  return ids.includes(eid);
}

function matchesQuery(e, q) {
  if (!q) return true;
  const hay = [
    e.title,
    e.subtitle,
    e.notes,
    e.description,
    e.work?.title,
    e.work?.notes,
    e.client?.name,
    e.client?.displayName,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(String(q).toLowerCase());
}

/* ---------------------------------- hooks ---------------------------------- */

/**
 * Fetch calendar events in a time window.
 */
export function useCalendarEvents(params = {}, options = {}) {
  const defaultWin = useMemo(() => defaultRange(), []);

  // IMPORTANT: do NOT let null/undefined override defaults.
  const merged = useMemo(() => {
    const p = params || {};
    const out = { ...defaultWin };

    if (p.from) out.from = p.from;
    if (p.to) out.to = p.to;

    for (const [k, v] of Object.entries(p)) {
      if (k === 'from' || k === 'to') continue;
      out[k] = v;
    }
    return out;
  }, [defaultWin, params]);

  // Build a normalized key shape so aliases don't split the cache
  const keyParams = normalizeForKey(merged);

  // Debug: show key + request-shape we’re about to pass down
  try {
    const keyDebug = JSON.parse(CAL_KEYS.list(keyParams)[2] || '[]');
    dlog('queryKey', keyDebug);
  } catch {
    dlog('queryKey', CAL_KEYS.list(keyParams));
  }
  dlog('requestParams', merged);

  // Service params are forwarded as-is; service normalizes for the backend
  const includeCancelled = keyParams.includeCancelled === true;
  const serviceParams = {
    ...merged,
    includeCancelled,
    // Use normalized q so aliases (search/title/query) work identically
    q: keyParams.q,
    // Pass-through name-based filters explicitly
    assigneeName: merged.assigneeName,
    clientName: merged.clientName,
  };

  // Log parameter *shapes* (this is where API mismatches typically show)
  dlog('paramShapes', {
    hasQ: !!serviceParams.q,
    typeIsArray: Array.isArray(serviceParams.type),
    statusIsArray: Array.isArray(serviceParams.status),
    priorityIsArray: Array.isArray(serviceParams.priority),
    assigneeIdType: Array.isArray(serviceParams.assigneeId)
      ? 'array'
      : typeof serviceParams.assigneeId,
    clientIdType: Array.isArray(serviceParams.clientId)
      ? 'array'
      : typeof serviceParams.clientId,
    assigneeName: serviceParams.assigneeName || undefined,
    clientName: serviceParams.clientName || undefined,
    includeCancelled: !!serviceParams.includeCancelled,
  });

  const query = useQuery({
    queryKey: CAL_KEYS.list(keyParams),
    queryFn: ({ signal }) => listEvents(serviceParams, { parseDates: true, signal }),
    placeholderData: keepPreviousData,
    staleTime: 0,
    ...options,
  });

  // Defensive filtering (ghosts)
  const rawItems = query.data?.items || [];
  const notDeleted = rawItems.filter((e) => !e?.softDeleted && !e?.deletedAt);

  // Optional fallback filter for assignee/client/q (debug aid)
  let items = notDeleted;
  if (FALLBACK_FILTERS) {
    const before = items.length;

    const assigneeIds = Array.isArray(serviceParams.assigneeId)
      ? serviceParams.assigneeId.map(String)
      : serviceParams.assigneeId
      ? String(serviceParams.assigneeId).split(',').map((s) => s.trim()).filter(Boolean)
      : [];

    const clientIds = Array.isArray(serviceParams.clientId)
      ? serviceParams.clientId.map(String)
      : serviceParams.clientId
      ? String(serviceParams.clientId).split(',').map((s) => s.trim()).filter(Boolean)
      : [];

    const afterAssignee = items.filter((e) => matchesAssignee(e, assigneeIds));
    const afterClient = afterAssignee.filter((e) => matchesClient(e, clientIds));
    const afterQ = afterClient.filter((e) => matchesQuery(e, serviceParams.q));

    // Log stepwise counts so we see what each filter is doing
    dlog('postFilter.counts', {
      before,
      afterAssignee: afterAssignee.length,
      afterClient: afterClient.length,
      afterQ: afterQ.length,
    });

    // If server ignored filters, these numbers will drop here.
    if ((assigneeIds.length || clientIds.length || serviceParams.q) && before === afterQ.length) {
      console.warn(
        '[calendar:serverIgnoredFilters?]',
        { assigneeIds, clientIds, q: serviceParams.q, before, afterQ: afterQ.length }
      );
    }

    items = afterQ;
  }

  // Deriveds
  const byDay = useMemo(() => groupEventsByDay(items, { parseDates: false }), [items]);
  const totals = useMemo(
    () => ({
      total: items.length,
      conflicts: items.filter((e) => e?.conflicts?.assigneeOverlap).length,
      minutes: items.reduce((acc, e) => acc + (computeDurationMinutes(e) || 0), 0),
    }),
    [items]
  );

  // Final debug summary
  if (DEBUG) {
    if (query.isFetching) dlog('status', { loading: query.isLoading, fetching: query.isFetching, error: query.error?.message });
    if (rawItems) dlog('responseRawCount', rawItems.length);
    dlog('itemsAfterFilter', items.length);
  }

  return { ...query, items, byDay, totals };
}

/**
 * Range-first wrapper for FullCalendar's datesSet payload.
 */
export function useWorkByRange(params = {}, options = {}) {
  const { start, end, from: pFrom, to: pTo, ...rest } = params || {};

  const rawFrom = start ?? pFrom ?? null;
  const rawTo = end ?? pTo ?? null;

  const debounceMs = Number.isFinite(options.debounceMs) ? options.debounceMs : 200;
  const from = useDebouncedValue(rawFrom, debounceMs);
  const to = useDebouncedValue(rawTo, debounceMs);

  const enabled = (options.enabled ?? true) && Boolean(from && to);

  return useCalendarEvents(
    { ...rest, from, to },
    {
      refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
      gcTime: options.gcTime ?? 300_000,
      ...options,
      enabled,
    }
  );
}

/**
 * Convenience hook for a single assignee’s schedule.
 */
export function useAssigneeCalendar(assigneeId, params = {}, options = {}) {
  const enabled = Boolean(assigneeId) && (options.enabled ?? true);
  return useCalendarEvents(
    { ...params, assignee: assigneeId },
    { ...options, enabled }
  );
}

/* --------------------------------- helpers --------------------------------- */

export function invalidateCalendarRange(queryClient, params = {}) {
  const hasWindow =
    params && typeof params.from === 'string' && typeof params.to === 'string';
  if (!hasWindow) {
    return queryClient.invalidateQueries({ queryKey: CAL_KEYS.root });
  }
  const keyParams = normalizeForKey(params);
  return queryClient.invalidateQueries({ queryKey: CAL_KEYS.list(keyParams) });
}
